import { useEffect, useState, type ReactElement } from 'react';
import { NavLink, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { api, can, getToken, getUser, roleLabel, SESSION_LOST, setSession, type SessionUser, canView } from './lib/api';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Posture from './pages/Posture';
import Purposes from './pages/Purposes';
import Notices from './pages/Notices';
import NoticeEditor from './pages/NoticeEditor';
import CollectionPoints from './pages/CollectionPoints';
import Forms from './pages/Forms';
import FormBuilder from './pages/FormBuilder';
import Cookies from './pages/Cookies';
import Campaigns from './pages/Campaigns';
import CampaignDetail from './pages/CampaignDetail';
import Ledger from './pages/Ledger';
import Capture from './pages/Capture';
import Propagation from './pages/Propagation';
import Processors from './pages/Processors';
import Exports from './pages/Exports';
import AuditLog from './pages/AuditLog';
import Principals from './pages/Principals';
import Requests from './pages/Requests';
import Settings from './pages/Settings';
import GuardianConsents from './pages/GuardianConsents';
import DeveloperApi from './pages/DeveloperApi';
import Connectors from './pages/Connectors';
import Nominations from './pages/Nominations';
import RetentionDue from './pages/RetentionDue';
import Today from './pages/Today';
import DataItems from './pages/DataItems';
import Approvals from './pages/Approvals';
import { TwoFactorDialog } from './components/Controls';

export default function App() {
  const [user, setUser] = useState<SessionUser | null>(getUser());

  // A session can die while the console is open — it expires, or the deployment
  // is re-seeded underneath it. Either way the operator should land on the
  // sign-in screen, not on an empty shell that quietly fails every request.
  useEffect(() => {
    const onLost = () => setUser(null);
    window.addEventListener(SESSION_LOST, onLost);
    return () => window.removeEventListener(SESSION_LOST, onLost);
  }, []);

  // Sessions opened before the organisation name travelled with the user
  // refresh it once, so the top bar names the organisation rather than nothing.
  useEffect(() => {
    if (!user || user.tenantName) return;
    api.get<{ user: SessionUser }>('/v1/me')
      .then((res) => { setSession(getToken() ?? '', res.user); setUser(res.user); })
      .catch(() => {});
  }, [user?.userId]);

  if (!user) return <Login onSignedIn={setUser} />;

  return (
    <div className="shell">
      <Sidebar user={user} onSignedOut={() => setUser(null)} />
      <main className="main">
          <div className="inner">
            <SectionTabs user={user} />
            <Routes>
              <Route path="/" element={<Dashboard />} />
              <Route path="/today" element={<Today />} />
              <Route path="/posture" element={guard(user, 'posture', <Posture />)} />
              <Route path="/purposes" element={guard(user, 'purposes', <Purposes />)} />
              <Route path="/data-items" element={guard(user, 'purposes', <DataItems />)} />
              <Route path="/notices" element={guard(user, 'notices', <Notices />)} />
              <Route path="/notices/:noticeId/versions/:version" element={guard(user, 'notices', <NoticeEditor />)} />
              <Route path="/collection-points" element={guard(user, 'collection_points', <CollectionPoints />)} />
              <Route path="/forms" element={guard(user, 'forms', <Forms />)} />
              <Route path="/forms/:formId" element={guard(user, 'forms', <FormBuilder />)} />
              <Route path="/cookies" element={guard(user, 'cookies', <Cookies />)} />
              <Route path="/campaigns" element={guard(user, 'campaigns', <Campaigns />)} />
              <Route path="/campaigns/:id" element={guard(user, 'campaigns', <CampaignDetail />)} />
              <Route path="/principals" element={guard(user, 'principals', <Principals />)} />
              <Route path="/principals/guardians" element={guard(user, 'principals', <GuardianConsents />)} />
              <Route path="/principals/:hash" element={guard(user, 'principals', <Principals />)} />
              <Route path="/requests" element={guard(user, 'requests', <Requests />)} />
              <Route path="/nominations" element={guard(user, 'requests', <Nominations />)} />
              <Route path="/ledger" element={guard(user, 'ledger', <Ledger />)} />
              <Route path="/capture" element={guard(user, 'consents', <Capture />)} />
              <Route path="/propagation" element={guard(user, 'processors', <Propagation />)} />
              <Route path="/processors" element={guard(user, 'processors', <Processors />)} />
              <Route path="/connectors" element={guard(user, 'processors', <Connectors />)} />
              <Route path="/exports" element={guard(user, 'ledger', <Exports />)} />
              <Route path="/audit" element={guard(user, 'audit', <AuditLog />)} />
              <Route path="/approvals" element={guard(user, 'approvals', <Approvals />)} />
              <Route path="/settings" element={guard(user, 'users', <Settings section="users" />)} />
              <Route path="/settings/roles" element={guard(user, 'users', <Settings section="roles" />)} />
              <Route path="/settings/languages" element={guard(user, 'languages', <Settings section="languages" />)} />
              <Route path="/settings/channels" element={guard(user, 'channels', <Settings section="channels" />)} />
              <Route path="/settings/security" element={guard(user, 'security', <Settings section="security" />)} />
              <Route path="/settings/api" element={guard(user, 'security', <DeveloperApi />)} />
              <Route path="/settings/retention" element={guard(user, 'purposes', <RetentionDue />)} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </div>
      </main>
    </div>
  );
}

/** A screen the role cannot view falls back to the dashboard. The API refuses the calls regardless. */
function guard(user: SessionUser, screen: string, element: ReactElement): ReactElement {
  return canView(user, screen) ? element : <Navigate to="/" replace />;
}

/**
 * Nine places, not eighteen. Pages that answer one question share a place and
 * appear as its tabs: notices sit with the purposes they state, the ledger with
 * its signed exports and the audit log.
 */
interface Tab { to: string; screen: string; label: string }
interface Section { key: string; label: string; tabs: Tab[]; also?: string[]; foot?: boolean }

const SECTIONS: Section[] = [
  { key: 'dashboard', label: 'Dashboard', tabs: [
    { to: '/', screen: 'dashboard', label: 'Overview' },
    { to: '/today', screen: 'dashboard', label: 'Today' },
    { to: '/posture', screen: 'posture', label: 'Compliance status' },
  ] },
  { key: 'purposes', label: 'Purposes', tabs: [
    { to: '/purposes', screen: 'purposes', label: 'Purposes' },
    { to: '/notices', screen: 'notices', label: 'Notices' },
    { to: '/data-items', screen: 'purposes', label: 'Data items' },
  ], also: ['/notices/'] },
  { key: 'collection', label: 'Collection', tabs: [
    { to: '/collection-points', screen: 'collection_points', label: 'Collection points' },
    { to: '/forms', screen: 'forms', label: 'Consent forms' },
    { to: '/cookies', screen: 'cookies', label: 'Website cookies' },
  ], also: ['/forms/'] },
  { key: 'campaigns', label: 'Campaigns', tabs: [{ to: '/campaigns', screen: 'campaigns', label: 'Retro data campaigns' }], also: ['/campaigns/'] },
  { key: 'principals', label: 'Data principals', tabs: [
    { to: '/principals', screen: 'principals', label: 'Data principals' },
    { to: '/principals/guardians', screen: 'principals', label: 'Children and guardians' },
  ], also: ['/principals/'] },
  { key: 'requests', label: 'Rights requests', tabs: [
    { to: '/requests', screen: 'requests', label: 'Rights requests' },
    { to: '/nominations', screen: 'requests', label: 'Nominations' },
  ] },
  { key: 'processors', label: 'Processors', tabs: [
    { to: '/processors', screen: 'processors', label: 'Processors' },
    { to: '/propagation', screen: 'processors', label: 'Delivery log' },
    { to: '/connectors', screen: 'processors', label: 'Connectors' },
  ] },
  { key: 'records', label: 'Records', tabs: [
    { to: '/ledger', screen: 'ledger', label: 'Consent ledger' },
    { to: '/capture', screen: 'consents', label: 'Record consent' },
    { to: '/exports', screen: 'ledger', label: 'Signed exports' },
    { to: '/audit', screen: 'audit', label: 'Audit log' },
  ] },
  { key: 'approvals', label: 'Approvals', foot: true, tabs: [{ to: '/approvals', screen: 'approvals', label: 'Approvals' }] },
  { key: 'settings', label: 'Settings', foot: true, tabs: [
    { to: '/settings', screen: 'users', label: 'Users' },
    { to: '/settings/roles', screen: 'users', label: 'Roles' },
    { to: '/settings/languages', screen: 'languages', label: 'Languages' },
    { to: '/settings/channels', screen: 'channels', label: 'Channels' },
    { to: '/settings/security', screen: 'security', label: 'Security' },
    { to: '/settings/api', screen: 'security', label: 'Developer API' },
    { to: '/settings/retention', screen: 'purposes', label: 'Retention due' },
  ] },
];

const visibleTabs = (user: SessionUser, s: Section) => s.tabs.filter((t) => t.screen === 'dashboard' || canView(user, t.screen));
function sectionOf(path: string): Section | undefined {
  return SECTIONS.find((s) => s.tabs.some((t) => t.to === path) || s.also?.some((p) => path.startsWith(p)));
}

/** The place is named once, above its tabs; the page's own heading steps aside. */
function SectionTabs({ user }: { user: SessionUser }) {
  const { pathname } = useLocation();
  const section = sectionOf(pathname);
  if (!section) return null;
  const tabs = visibleTabs(user, section);
  if (tabs.length < 2 || !tabs.some((t) => t.to === pathname)) return null;
  return (
    <div className="section-head">
      <h1>{section.label}</h1>
      <div className="section-tabs" role="tablist">
        {tabs.map((t) => (
          <NavLink key={t.to} to={t.to} end className={({ isActive }) => (isActive ? 'section-tab on' : 'section-tab')}>{t.label}</NavLink>
        ))}
      </div>
    </div>
  );
}

/** Same rail as the discovery console: brand at the top, places, the signed-in user at the foot. */
function Sidebar({ user, onSignedOut }: { user: SessionUser; onSignedOut: () => void }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [twoFactor, setTwoFactor] = useState(false);
  const current = sectionOf(pathname)?.key;
  const link = (sec: Section) => {
    const tabs = visibleTabs(user, sec);
    if (!tabs.length) return null;
    return <NavLink key={sec.key} to={tabs[0]!.to} className={current === sec.key ? 'side-link side-link-on' : 'side-link'} end>{sec.label}</NavLink>;
  };
  async function signOut() {
    try { await api.logout(); } finally {
      onSignedOut();
      navigate('/');
    }
  }
  return (
    <nav className="side" aria-label="Sections">
      <div className="side-brand">
        <img src="./wekalp-logo-white.png" alt="Wekalp" />
        <span>Consent</span>
      </div>
      <div className="side-nav">
        {SECTIONS.filter((s) => !s.foot).map(link)}
        <div className="side-spacer" />
        {SECTIONS.filter((s) => s.foot).map(link)}
      </div>
      <div className="side-foot">
        <div className="side-user">
          <div className="side-user-name">{user.name || user.email}</div>
          <div className="side-user-role">{roleLabel(user.role)}{user.tenantName ? ` · ${user.tenantName}` : ''}</div>
        </div>
        <button className="ghost small" onClick={() => setTwoFactor(true)}>Two-factor sign-in</button>
        <button className="ghost small" onClick={signOut}>Sign out</button>
        {twoFactor && <TwoFactorDialog onClose={() => setTwoFactor(false)} />}
      </div>
    </nav>
  );
}
